/**
 * SOLID Review
 * S — Strong. One concern: what the table decisions read — the store's data, shaped and normalised as the
 *     table snapshot, and that table with the rules it is played by.
 * O — Good. A new table field is one line here and one in the snapshot schema.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Two operations: the store's data in, the snapshot out; the client's snapshot in, a table
 *     decision's input out.
 * D — Strong. Types only: the store's (declared here, in application/state) and the Domain's schemas.
 */
import type { TGameStoreData } from './gameStoreTypes';
import type { IStateClientTable, IEntityClientRoom, IEntityClientSeat, IPropsClientTableInput } from '../../domain/ports/schemas/ClientTable';
import type { IPropsClientInput } from '../../domain/ports/schemas/ClientChanges';
import { PlayerStatus, MonetizationTrack } from '../../domain/uml-components/primitives';
import type { IEntityRoom, IEntityPlayer } from '../../domain/uml-components/entities';

const STATUSES: readonly string[] = Object.freeze(Object.values(PlayerStatus));
const TRACKS: readonly string[] = Object.freeze(Object.values(MonetizationTrack));

/**
 * THE STORE AS THE TABLE DECISIONS SEE IT (frontend strict-ports plan, FE1, 2026-10-03). Two readers,
 * which is why it lives in the Application rather than in either of them:
 *
 *   - Infrastructure's `ClientState.snapshot` — what every decision is handed;
 *   - the store's interim middleware, which derives the screen's values after a write that did not
 *     come through a decision (the FE2–FE5 flows, until FE6).
 *
 * NORMALISED, NOT TRUSTED (FE1e). The store is TYPED, but until FE6 not every writer is: the FE2–FE5
 * flows still copy unparsed reads into it. Found by the recovery suite: a restore whose read lacked
 * `pendingClaim.wasBlind` stored `claimWasBlind: undefined`. One such value fails the snapshot's
 * contract, and with it EVERY table decision, silently — the table stops. So each value is read as its
 * type: a boolean is `=== true`, a number must be finite, a string must be a string, and anything else
 * reads as the field's empty value. The fault then degrades to that one field, not to the whole table.
 * An enum has no empty value: an unknown seat status or track reads as what the roster gives a seat it
 * knows nothing about (`buildRosterFromRaw`: ACTIVE, FREE) — no decision reads either, they are carried.
 * A seat without an id, or a room without one, is not a seat or a room, and is left out.
 *
 * Every value is copied, never shared: a snapshot is JSON on its way through the contract, and a caller
 * that edited one must not edit the store.
 */
export class ClientTableProjector {
  project(store: TGameStoreData): IStateClientTable {
    const table: IStateClientTable = {
      // '' is "no identity" spelled wrong; the snapshot's schema refuses it, so it reads as none.
      localPlayerId: this.text(store.localUser?.playerId) || null,
      room: store.room === null || store.room === undefined || !this.text(store.room.roomId) ? null : this.roomOf(store.room),
      phase: store.phase,
      activeRoundId: this.textOrNull(store.activeRoundId),
      activeRollerId: this.textOrNull(store.activeRollerId),
      activeChallengerId: this.textOrNull(store.activeChallengerId),
      lastDiceRoll: store.lastDiceRoll === null || store.lastDiceRoll === undefined ? null : {
        die1: this.int(store.lastDiceRoll.die1),
        die2: this.int(store.lastDiceRoll.die2),
        score: this.int(store.lastDiceRoll.score),
        isMeyer: store.lastDiceRoll.isMeyer === true,
        isDouble: store.lastDiceRoll.isDouble === true,
      },
      minimumClaimScore: this.int(store.minimumClaimScore),
      lastClaimedScore: this.intOrNull(store.lastClaimedScore),
      claimWasBlind: store.claimWasBlind === true,
      diceRevealed: store.diceRevealed === true,
      opponentClaim: store.opponentClaim === null || store.opponentClaim === undefined ? null : {
        score: this.int(store.opponentClaim.score),
        label: this.text(store.opponentClaim.label),
      },
      revealResult: store.revealResult === null || store.revealResult === undefined ? null : {
        losingPlayerId: this.text(store.revealResult.losingPlayerId),
        wasBluff: store.revealResult.wasBluff === true,
        claimedScore: this.int(store.revealResult.claimedScore),
        trueScore: this.int(store.revealResult.trueScore),
        die1: this.int(store.revealResult.die1),
        die2: this.int(store.revealResult.die2),
        newRollerId: this.text(store.revealResult.newRollerId),
      },
      decisionCountdown: store.decisionCountdown === null || store.decisionCountdown === undefined ? null : {
        roundId: this.text(store.decisionCountdown.roundId),
        challengerId: this.text(store.decisionCountdown.challengerId),
        secondsRemaining: this.int(store.decisionCountdown.secondsRemaining),
      },
      playerLives: Object.fromEntries(Object.entries(store.playerLives ?? {}).map(([id, lives]) => [id, this.int(lives)])),
      suspendedPlayerIds: (store.suspendedPlayerIds ?? []).filter((id): id is string => typeof id === 'string'),
      roomEnded: store.roomEnded === true,
      tableVersion: store.tableVersion === null || store.tableVersion === undefined || !this.text(store.tableVersion.roomId) ? null : {
        roomId: store.tableVersion.roomId,
        version: this.int(store.tableVersion.version),
      },
      connectionState: store.connectionState ?? 'disconnected',
      connectionWasDown: store.connectionWasDown === true,
      lastGameActivityAt: this.int(store.lastGameActivityAt),
    };
    return table;
  }

  /**
   * A TABLE DECISION'S INPUT (frontend-domain-oo P2, 2026-10-26): the table, and the rules it is played by — the
   * session's dice ranking. A table decision is not handed the session, and its claim lists, labels, claimed dice
   * and Meyer pre-check read the ranking. Here rather than in `ClientSteps`, which only forwards it: a shell does
   * not read the snapshots (`lint:decisions`).
   */
  inputOf(input: IPropsClientInput): IPropsClientTableInput {
    const tableInput: IPropsClientTableInput = { table: input.table, diceRanking: input.session.diceRanking };
    return tableInput;
  }

  private text(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private textOrNull(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
  }

  private int(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : 0;
  }

  private intOrNull(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : null;
  }

  private oneOf<T extends string>(members: readonly string[], value: unknown, fallback: T): T {
    return typeof value === 'string' && members.includes(value) ? (value as T) : fallback;
  }

  private roomOf(room: IEntityRoom): IEntityClientRoom {
    return {
      roomId: room.roomId,
      roomCode: this.text(room.roomCode),
      hostPlayerId: this.text(room.hostPlayerId),
      players: (room.players ?? []).filter((p) => this.text(p?.id) !== '').map((p) => this.seatOf(p)),
      maxPlayers: this.int(room.maxPlayers),
      minPlayers: this.int(room.minPlayers),
      isGameActive: room.isGameActive === true,
    };
  }

  private seatOf(p: IEntityPlayer): IEntityClientSeat {
    return {
      id: p.id,
      displayName: this.text(p.displayName),
      avatarUrl: this.textOrNull(p.avatarUrl),
      status: this.oneOf(STATUSES, p.status, PlayerStatus.ACTIVE),
      lives: { current: this.int(p.lives?.current), max: this.int(p.lives?.max), isEliminated: p.lives?.isEliminated === true },
      monetization: {
        track: this.oneOf(TRACKS, p.monetization?.track, MonetizationTrack.FREE),
        matchCredits: this.int(p.monetization?.matchCredits),
        hasPremiumAccess: p.monetization?.hasPremiumAccess === true,
      },
      isLocalPlayer: p.isLocalPlayer === true,
      isCurrentTurn: p.isCurrentTurn === true,
      seatIndex: this.int(p.seatIndex),
    };
  }
}
